import type { DiscoveryContentTypeConfig } from '@terra/shared-asset-processing';
import { resolveDiscoveryContentTypes } from '@terra/shared-asset-processing';
import type {
  CrawlerPeriodicSettingsDto,
  ScheduledReportRule,
} from '@terra/shared-contracts-applications';
import { DEFAULT_CRAWLER_PERIODIC_SETTINGS } from '@terra/shared-contracts-applications';
import type { SignalCategoryType } from '@terra/shared-schemas-orchestrator';
import {
  DEFAULT_DAYS_AUTOMATIC_EXECUTIONS,
  WORKING_HOURS_MAX,
  WORKING_HOURS_MIN,
  type AutomationSchedule,
  type ExecutionsConfigurationCardChange,
} from '@terra/ui-design-system';

export const DEFAULT_AUTOMATION_SCHEDULE: AutomationSchedule = {
  daysOfWeek: [...DEFAULT_DAYS_AUTOMATIC_EXECUTIONS],
  startHour: WORKING_HOURS_MIN,
  endHour: WORKING_HOURS_MAX,
  timezone: null,
};

export type AutomationSettingsSource = {
  crawlerPeriodic?: CrawlerPeriodicSettingsDto | null;
  smokeTestEnabled?: boolean | null;
  automationSchedule?: AutomationSchedule | null;
  supportsParallelExecution?: boolean | null;
  signalCategoriesInScope?: SignalCategoryType[] | null;
  discoveryContentTypes?: DiscoveryContentTypeConfig[] | null;
};

export type AutomationSettingsValues = {
  crawlerPeriodic: CrawlerPeriodicSettingsDto;
  smokeTestEnabled: boolean;
  limitExecutionTimeEnabled: boolean;
  automationSchedule: AutomationSchedule;
  supportsParallelExecution: boolean;
  signalCategoriesInScope: SignalCategoryType[];
  discoveryContentTypes: DiscoveryContentTypeConfig[];
};

export type AutomationSettingsUpdate = Partial<{
  crawlerPeriodic: CrawlerPeriodicSettingsDto;
  smokeTestEnabled: boolean;
  automationSchedule: AutomationSchedule | null;
  supportsParallelExecution: boolean;
  signalCategoriesInScope: SignalCategoryType[];
  discoveryContentTypes: DiscoveryContentTypeConfig[];
}>;

export type ScheduledReportsSource = {
  scheduledReportsEnabled?: boolean | null;
  scheduledReportRules?: ScheduledReportRule[] | null;
  nextScheduledReportAt?: Date | string | null;
  activeScheduledReportRuleCount?: number | null;
};

export type ScheduledReportsValues = {
  enabled: boolean;
  rules: ScheduledReportRule[];
  nextScheduledReportAt: Date | string | null;
  activeScheduledReportRuleCount: number | undefined;
};

export const toAutomationSettingsValues = (
  source: AutomationSettingsSource | null | undefined,
): AutomationSettingsValues => ({
  crawlerPeriodic: source?.crawlerPeriodic ?? DEFAULT_CRAWLER_PERIODIC_SETTINGS,
  smokeTestEnabled: source?.smokeTestEnabled ?? false,
  limitExecutionTimeEnabled: source?.automationSchedule !== null,
  automationSchedule: source?.automationSchedule ?? DEFAULT_AUTOMATION_SCHEDULE,
  supportsParallelExecution: source?.supportsParallelExecution ?? true,
  signalCategoriesInScope: source?.signalCategoriesInScope ?? [],
  discoveryContentTypes: resolveDiscoveryContentTypes(
    source?.discoveryContentTypes ?? undefined,
  ),
});

export const toScheduledReportsValues = (
  source: ScheduledReportsSource | null | undefined,
): ScheduledReportsValues => ({
  enabled: Boolean(source?.scheduledReportsEnabled),
  rules: source?.scheduledReportRules ?? [],
  nextScheduledReportAt: source?.nextScheduledReportAt ?? null,
  activeScheduledReportRuleCount:
    source?.activeScheduledReportRuleCount ?? undefined,
});

const toAutomationScheduleUpdate = ({
  change,
  limitExecutionTimeEnabled,
}: {
  change: ExecutionsConfigurationCardChange;
  limitExecutionTimeEnabled: boolean;
}): AutomationSettingsUpdate => {
  if (typeof change.limitExecutionTimeEnabled === 'boolean') {
    return {
      automationSchedule: change.limitExecutionTimeEnabled
        ? (change.automationSchedule ?? DEFAULT_AUTOMATION_SCHEDULE)
        : null,
    };
  }

  if (change.automationSchedule !== undefined && limitExecutionTimeEnabled) {
    return { automationSchedule: change.automationSchedule };
  }

  return {};
};

export const toExecutionsSettingsUpdate = ({
  change,
  limitExecutionTimeEnabled,
}: {
  change: ExecutionsConfigurationCardChange;
  limitExecutionTimeEnabled: boolean;
}): AutomationSettingsUpdate => ({
  ...(typeof change.smokeTestEnabled === 'boolean'
    ? { smokeTestEnabled: change.smokeTestEnabled }
    : {}),
  ...(typeof change.supportsParallelExecution === 'boolean'
    ? { supportsParallelExecution: change.supportsParallelExecution }
    : {}),
  ...toAutomationScheduleUpdate({ change, limitExecutionTimeEnabled }),
});

type AutomationSettingsKey = keyof AutomationSettingsUpdate;

export const toAutomationSettingsRevert = <
  T extends Pick<AutomationSettingsSource, AutomationSettingsKey>,
>({
  previous,
  update,
}: {
  previous: T;
  update: AutomationSettingsUpdate;
}): Partial<Pick<T, AutomationSettingsKey>> => ({
  ...(Object.hasOwn(update, 'crawlerPeriodic')
    ? { crawlerPeriodic: previous.crawlerPeriodic }
    : {}),
  ...(Object.hasOwn(update, 'smokeTestEnabled')
    ? { smokeTestEnabled: previous.smokeTestEnabled }
    : {}),
  ...(Object.hasOwn(update, 'automationSchedule')
    ? { automationSchedule: previous.automationSchedule }
    : {}),
  ...(Object.hasOwn(update, 'supportsParallelExecution')
    ? { supportsParallelExecution: previous.supportsParallelExecution }
    : {}),
  ...(Object.hasOwn(update, 'signalCategoriesInScope')
    ? { signalCategoriesInScope: previous.signalCategoriesInScope }
    : {}),
  ...(Object.hasOwn(update, 'discoveryContentTypes')
    ? { discoveryContentTypes: previous.discoveryContentTypes }
    : {}),
});
